import {WithdrawalMinimum} from "../components/WithdrawalMinimum";
import React, {useCallback, useEffect, useState} from "react";
import {
    ApiError,
    createTeamWithdrawalRequest,
    getTeamBalance,
    getTeamBalanceMovements,
    getTeamPayoutWallets,
    getTeamWithdrawalRequest,
} from "../api";
import type {
    BalanceMovementItem,
    BalanceMovementsResponse,
    TeamBalanceResponse,
    TeamPayoutWallet,
    TeamWithdrawalRequestResponse,
} from "../api";
import {useAuth} from "../auth";
import {BalanceMovementsTable, Modal} from "../components";
import {formatRelativeTime, truncateAmount} from "../utils";
import {apiErrorCode, messageForCode} from "../api/error-codes";
import type {ErrorCode} from "../generated/openapi-contract";

/**
 * Balance page — командный баланс из GET /balance/team, его движения и запрос
 * вывода.
 *
 * Разбивки по мерчантам здесь нет намеренно (решение владельца 01.09.2026):
 * трейдер видит один баланс команды и сумму к выводу, а деление по мерчантам
 * остаётся полностью внутренним — снапшот запроса режется по срезам и гасится
 * по ним же, просто наружу это больше не показывается.
 *
 * Поля `amount` и `withdrawable` приходят готовыми десятичными строками
 * (balance.FormatAtomicAmount) и рисуются как есть: в `number` для денежной
 * арифметики они не переводятся нигде на странице.
 */
export function BalancePage(): JSX.Element {
    const {token} = useAuth();
    const [balance, setBalance] = useState<TeamBalanceResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [missing, setMissing] = useState(false);
    const [movements, setMovements] = useState<BalanceMovementItem[]>([]);
    const [movementsCurrency, setMovementsCurrency] = useState("");
    const [movementsNextOffset, setMovementsNextOffset] = useState<number | null>(null);
    const [movementsLoading, setMovementsLoading] = useState(false);
    const [movementsLoadingMore, setMovementsLoadingMore] = useState(false);
    const [movementsError, setMovementsError] = useState("");
    const [withdrawal, setWithdrawal] = useState<TeamWithdrawalRequestResponse | null>(null);
    const [withdrawalError, setWithdrawalError] = useState("");
    const [withdrawalOpen, setWithdrawalOpen] = useState(false);
    const [withdrawalLoading, setWithdrawalLoading] = useState(false);
    const [withdrawalAmount, setWithdrawalAmount] = useState("");
    // Кошельки команды для окна вывода; null — ещё не загружены или не
    // загрузились. Тогда запрос уходит без выбора, и сервер берёт самый новый.
    const [wallets, setWallets] = useState<TeamPayoutWallet[] | null>(null);
    const [walletId, setWalletId] = useState("");

    const applyMovements = useCallback((resp: BalanceMovementsResponse, append: boolean): void => {
        setMovements((prev) => append ? [...prev, ...resp.items] : resp.items);
        setMovementsCurrency(resp.currency);
        setMovementsNextOffset(resp.nextOffset ?? null);
    }, []);

    const loadMovements = useCallback(async (offset = 0, append = false): Promise<void> => {
        if (!token) {
            setMovements([]);
            setMovementsCurrency("");
            setMovementsNextOffset(null);
            setMovementsError("");
            return;
        }
        if (append) {
            setMovementsLoadingMore(true);
        } else {
            setMovementsLoading(true);
        }
        setMovementsError("");
        try {
            const resp = await getTeamBalanceMovements(token, {offset});
            applyMovements(resp, append);
        } catch (err) {
            if (!append) {
                setMovements([]);
                setMovementsCurrency("");
                setMovementsNextOffset(null);
            }
            setMovementsError(formatBalanceError(err));
        } finally {
            setMovementsLoading(false);
            setMovementsLoadingMore(false);
        }
    }, [applyMovements, token]);

    // Запрос вывода: активный, а если его нет — последний закрытый. 404 здесь
    // штатен и означает «команда ещё ни разу не запрашивала вывод», поэтому в
    // ошибку он не превращается.
    const loadWithdrawal = useCallback(async (): Promise<void> => {
        if (!token) {
            setWithdrawal(null);
            setWithdrawalError("");
            return;
        }
        try {
            setWithdrawal(await getTeamWithdrawalRequest(token));
            setWithdrawalError("");
        } catch (err) {
            setWithdrawal(null);
            if (!(err instanceof ApiError && err.status === 404)) {
                setWithdrawalError(formatBalanceError(err));
            }
        }
    }, [token]);

    const refresh = useCallback(async () => {
        if (!token) {
            setBalance(null);
            setMissing(false);
            setError("");
            setMovements([]);
            setMovementsCurrency("");
            setMovementsNextOffset(null);
            setMovementsError("");
            setWithdrawal(null);
            setWithdrawalError("");
            return;
        }
        setLoading(true);
        setError("");
        setSuccess("");
        setMissing(false);
        try {
            const resp = await getTeamBalance(token);
            setBalance(resp);
            await Promise.all([loadMovements(), loadWithdrawal()]);
        } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
                setBalance(null);
                setMissing(true);
                setMovements([]);
                setMovementsCurrency("");
                setMovementsNextOffset(null);
            } else {
                setBalance(null);
                setMissing(false);
                setError(formatBalanceError(err));
            }
        } finally {
            setLoading(false);
        }
    }, [loadMovements, loadWithdrawal, token]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const pendingWithdrawal = withdrawal && withdrawal.status === "pending" ? withdrawal : null;
    // Доступно к выводу — сумма положительных срезов, и она бывает больше
    // самого баланса: долг команды мерчанту выводом не гасится. Считает её
    // бэкенд тем же кодом, которым потом проверяет сумму запроса, поэтому
    // подсказка и отказ разойтись не могут.
    const withdrawable = balance?.withdrawable ?? "0";
    // Кнопка активна, когда выводить есть что и активного запроса нет.
    // Кошельки грузятся уже в окне: отсутствие привязки объясняет отказ 409,
    // а не заранее погашенная кнопка.
    const canRequestWithdrawal = Boolean(balance) && !pendingWithdrawal && isPositiveAmount(withdrawable);

    const openWithdrawalConfirm = (): void => {
        setWithdrawalError("");
        // Поле открывается заполненным всей доступной суммой: чаще всего
        // выводят именно её, а частичный вывод — правка уже готового числа.
        setWithdrawalAmount(withdrawable);
        setWithdrawalOpen(true);
        setWallets(null);
        setWalletId("");
        if (!token) return;
        getTeamPayoutWallets(token)
            .then((items) => {
                setWallets(items);
                // Сервер отдаёт новый кошелёк первым — он и выбран по умолчанию.
                setWalletId(items[0]?.id ?? "");
            })
            .catch(() => setWallets(null));
    };

    const closeWithdrawalConfirm = (): void => {
        if (withdrawalLoading) return;
        setWithdrawalOpen(false);
    };

    const confirmWithdrawal = async (): Promise<void> => {
        if (!token || withdrawalLoading) return;
        const shapeError = checkWithdrawalAmountShape(withdrawalAmount);
        if (shapeError) {
            setWithdrawalError(shapeError);
            return;
        }
        setWithdrawalLoading(true);
        setWithdrawalError("");
        setError("");
        setSuccess("");
        try {
            const created = await createTeamWithdrawalRequest(token, withdrawalAmount, walletId || undefined);
            setWithdrawal(created);
            setWithdrawalOpen(false);
            setSuccess(`Запрос вывода на ${created.amount} ${created.currency} создан. Долг закроется, когда деньги придут на адрес.`);
        } catch (err) {
            setWithdrawalError(formatWithdrawalError(err, withdrawable, balance?.currency ?? ""));
        } finally {
            setWithdrawalLoading(false);
        }
    };

    return (
        <div className="page balance-page">
            <div className="page-header">
                <h2>Balance</h2>
                <div className="page-actions">
                    <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={refresh}
                        disabled={loading}
                    >
                        {loading ? "Refreshing..." : "Refresh"}
                    </button>
                    <button
                        className="btn btn-primary btn-sm"
                        type="button"
                        onClick={openWithdrawalConfirm}
                        disabled={!canRequestWithdrawal || loading || withdrawalLoading}
                    >
                        Запросить вывод
                    </button>
                </div>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-info">{success}</div>}

            {missing && (
                <div className="empty-state">
                    Team balance not found. Contact an administrator to create a
                    team balance.
                </div>
            )}

            {balance && (
                <section className="balance-card">
                    <div className="balance-line">
                        <span>Team balance</span>
                        <strong className="mono" title={`${balance.amount} ${balance.currency}`}>
                            {truncateAmount(balance.amount)} {balance.currency}
                        </strong>
                    </div>
                    <div className="balance-line">
                        <span>Доступно к выводу</span>
                        <strong className="mono" title={`${withdrawable} ${balance.currency}`}>
                            {truncateAmount(withdrawable)} {balance.currency}
                        </strong>
                    </div>
                    <div className="balance-line">
                        <span>Last reset</span>
                        <strong className="mono" title={balance.lastBalanceResetDate || undefined}>
                            {formatRelativeTime(balance.lastBalanceResetDate)}
                        </strong>
                    </div>
                </section>
            )}

            {!balance && !missing && !error && loading && (
                <div className="empty-state">Loading balance...</div>
            )}

            {withdrawalError && !withdrawalOpen && <div className="alert alert-error">{withdrawalError}</div>}

            {withdrawal && (
                <section className="balance-card withdrawal-card">
                    <div className="balance-line">
                        <span>{withdrawalTitle(withdrawal.status)}</span>
                        <strong className="mono" title={`${withdrawal.amount} ${withdrawal.currency}`}>
                            {truncateAmount(withdrawal.amount)} {withdrawal.currency}
                        </strong>
                    </div>
                    {withdrawal.status === "pending" && (
                        <div className="balance-line">
                            <span>Поступило</span>
                            <strong className="mono" title={`${withdrawal.received} ${withdrawal.currency}`}>
                                {truncateAmount(withdrawal.received)} {withdrawal.currency}
                            </strong>
                        </div>
                    )}
                    {withdrawal.status === "completed" && withdrawal.reset != null && (
                        <div className="balance-line">
                            <span>Сброшено</span>
                            <strong className="mono" title={`${withdrawal.reset} ${withdrawal.currency}`}>
                                {truncateAmount(withdrawal.reset)} {withdrawal.currency}
                            </strong>
                        </div>
                    )}
                    <div className="balance-line">
                        <span>Адрес ({withdrawal.network})</span>
                        <strong className="mono" title={withdrawal.address}>{withdrawal.address}</strong>
                    </div>
                    <div className="balance-line">
                        <span>Запрошен</span>
                        <strong className="mono" title={withdrawal.createdAt}>
                            {formatRelativeTime(withdrawal.createdAt)}
                        </strong>
                    </div>
                </section>
            )}

            {balance && (
                <BalanceMovementsTable
                    items={movements}
                    currency={movementsCurrency || balance.currency}
                    loading={movementsLoading}
                    loadingMore={movementsLoadingMore}
                    error={movementsError}
                    hasMore={movementsNextOffset !== null}
                    onLoadMore={() => {
                        if (movementsNextOffset !== null) {
                            void loadMovements(movementsNextOffset, true);
                        }
                    }}
                    counterpartyLabel="Мерчант"
                />
            )}

            <Modal
                open={withdrawalOpen}
                title="Запросить вывод баланса?"
                onClose={closeWithdrawalConfirm}
                disableClose={withdrawalLoading}
                sizeClassName="modal-sm"
            >
                <p className="modal-hint">
                    Сумма фиксируется прямо сейчас: обнулится ровно она, а заработанное после запроса
                    останется на балансе. Долг закроется сам, когда деньги придут на кошелёк команды;
                    отправляет их администратор вручную.
                </p>
                {balance && (
                    <form
                        className="onboarding-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            void confirmWithdrawal();
                        }}
                    >
                        <div className="form-group">
                            <WithdrawalMinimum audience="trader"/>
                            <label htmlFor="withdrawal-amount">Сумма вывода ({balance.currency})</label>
                            <input
                                id="withdrawal-amount"
                                className="mono"
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                spellCheck={false}
                                value={withdrawalAmount}
                                onChange={(e) => setWithdrawalAmount(e.target.value)}
                                disabled={withdrawalLoading}
                            />
                            <small>
                                Доступно к выводу: {withdrawable} {balance.currency}.{" "}
                                <button
                                    className="btn btn-ghost btn-sm"
                                    type="button"
                                    onClick={() => setWithdrawalAmount(withdrawable)}
                                    disabled={withdrawalLoading}
                                >
                                    Весь баланс
                                </button>
                            </small>
                        </div>
                        {wallets && wallets.length > 1 && (
                            <div className="payout-wallet-field">
                                <span className="payout-wallet-label" id="withdrawal-wallet-label">Кошелёк для вывода</span>
                                <div
                                    className="mode-radio-group payout-wallet-choice"
                                    role="radiogroup"
                                    aria-labelledby="withdrawal-wallet-label"
                                >
                                    {wallets.map((wallet) => (
                                        <label
                                            key={wallet.id}
                                            className={`mode-radio-card ${walletId === wallet.id ? "selected" : ""}`}
                                        >
                                            <input
                                                type="radio"
                                                name="withdrawal-wallet"
                                                value={wallet.id}
                                                checked={walletId === wallet.id}
                                                onChange={() => setWalletId(wallet.id)}
                                                disabled={withdrawalLoading}
                                            />
                                            <span>
                                                <strong className="mono">{wallet.address}</strong>
                                                <small>{wallet.network.toUpperCase()}</small>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="confirm-summary">
                            {wallets && wallets.length === 1 && (
                                <div>
                                    <span>Кошелёк</span>
                                    <strong className="mono" title={wallets[0].address}>{wallets[0].address}</strong>
                                </div>
                            )}
                            <div>
                                <span>Будет запрошено</span>
                                <strong className="mono" title={`${withdrawalAmount.trim()} ${balance.currency}`}>
                                    {withdrawalAmount.trim() || "—"} {balance.currency}
                                </strong>
                            </div>
                        </div>
                        {withdrawalError && <div className="alert alert-error">{withdrawalError}</div>}
                        <div className="modal-actions">
                            <button
                                className="btn btn-ghost"
                                type="button"
                                onClick={closeWithdrawalConfirm}
                                disabled={withdrawalLoading}
                            >
                                Отмена
                            </button>
                            <button
                                className="btn btn-primary"
                                type="submit"
                                disabled={withdrawalLoading}
                            >
                                {withdrawalLoading ? "Отправка..." : "Запросить"}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>
        </div>
    );
}

// Положительность решается по строке, а не по parseFloat: суммы приходят
// готовыми десятичными строками и в number не переводятся нигде на странице.
function isPositiveAmount(amount: string | undefined): boolean {
    if (!amount) return false;
    const value = amount.trim();
    if (!value || value.startsWith("-")) return false;
    return /[1-9]/.test(value);
}

// Проверка ФОРМЫ числа, и только её. Хватает ли этой суммы, решает бэкенд по
// заблокированному балансу: страница видит остаток на момент загрузки, а
// деньги могли уйти секунду назад.
function checkWithdrawalAmountShape(raw: string): string {
    const value = raw.trim();
    if (!value) return "Укажите сумму вывода.";
    if (!/^\d+(\.\d+)?$/.test(value)) {
        return "Сумма — положительное число, разделитель — точка.";
    }
    const dot = value.indexOf(".");
    if (dot >= 0 && value.length - dot - 1 > 6) {
        return "Не больше шести знаков после точки.";
    }
    if (!isPositiveAmount(value)) return "Сумма должна быть больше нуля.";
    return "";
}

function withdrawalTitle(status: TeamWithdrawalRequestResponse["status"]): string {
    switch (status) {
        case "pending":
            return "Запрошен вывод";
        case "completed":
            return "Вывод выполнен";
        default:
            return "Запрос вывода отменён";
    }
}

// Отказы различает код в теле ошибки (ErrResponse.code), а не статус:
// «нет адреса», «кошелёк отвязан», «запрос уже есть», «выводить нечего» и
// «столько нельзя» — разные 409, и трейдеру нужно знать, какой именно.
const WITHDRAWAL_REFUSALS: Partial<Record<ErrorCode, string>> = {
    withdrawal_below_minimum: "Сумма ниже действующего минимума USDT. Увеличьте сумму вывода.",
    no_payout_address: "Команде не привязан адрес вывода. Обратитесь к администратору.",
    payout_address_unavailable: "Этот кошелёк команды уже отвязан. Откройте окно заново и выберите другой.",
    withdrawal_request_exists: "У команды уже есть активный запрос вывода.",
    nothing_to_withdraw: "Выводить нечего: положительного долга по мерчантам нет.",
};

function formatWithdrawalError(err: unknown, available: string, currency: string): string {
    if (apiErrorCode(err) === "amount_exceeds_available") {
        return `Столько вывести нельзя: доступно ${available} ${currency}.`.trim();
    }
    return messageForCode(err, WITHDRAWAL_REFUSALS) ?? formatBalanceError(err);
}

function formatBalanceError(err: unknown): string {
    if (err instanceof ApiError) {
        if (err.status === 401) return "Sign in is required.";
        if (err.status === 403) return "This balance is not available.";
        return err.message;
    }
    if (err instanceof Error) return err.message;
    return "Could not load balance.";
}
